/**
 * Responsibility: decide when a live camera should take the shot itself.
 *
 * **Holding a card up and tapping a button are two hands.** Identifying a
 * shoebox is a hundred cards one after another, so the useful shape is:
 * hold a card in the frame, hear it land in the list, put the next one up.
 * That only works if the app decides when to look.
 *
 * Three rules, and each of them exists to stop a specific waste:
 *
 * 1. **Sharp for two consecutive samples.** One sharp frame happens mid
 *    movement; two, 350ms apart, means the hand has stopped. Scanning a
 *    blurred frame costs a round trip to a two-core box for an answer
 *    that was never going to be right.
 * 2. **Not while a scan is in flight.** The hub does one card in a second
 *    or so and the stream produces three frames in that time.
 * 3. **Once per card.** After a shot it disarms, and only a scene change
 *    - the card being taken out of the frame and another put in - arms it
 *    again. Without this a card left on the desk is scanned for ever.
 */
import { useCallback, useMemo, useRef } from 'react';

/** Consecutive sharp samples before a frame counts as held still. */
const STEADY_SAMPLES = 2;

/**
 * Mean grey difference that counts as "a different card is in the frame".
 *
 * Hand shake and auto-exposure move this a little on every sample; a card
 * leaving the frame moves it a lot. Set above the first and well below
 * the second.
 */
const SCENE_CHANGED = 6;

export type AutoCapture = {
  /** Feed one sample in; true means take the shot now. */
  offer: (stats: { sharpness: number; change: number }, ready: boolean) => boolean;
  /** Arm it again by hand, for "scan another" after a result. */
  rearm: () => void;
};

export function useAutoCapture(sharpEnough: number): AutoCapture {
  const steady = useRef(0);
  const armed = useRef(true);

  const offer = useCallback(
    (stats: { sharpness: number; change: number }, ready: boolean) => {
      if (stats.change >= SCENE_CHANGED) {
        // The frame moved on. Whatever was scanned is gone, so the next
        // still moment is a new card and worth looking at.
        armed.current = true;
        steady.current = 0;
        return false;
      }
      if (!ready || !armed.current) return false;

      steady.current = stats.sharpness >= sharpEnough ? steady.current + 1 : 0;
      if (steady.current < STEADY_SAMPLES) return false;

      steady.current = 0;
      armed.current = false;
      return true;
    },
    [sharpEnough],
  );

  const rearm = useCallback(() => {
    armed.current = true;
    steady.current = 0;
  }, []);

  // **One object, for the life of the component.** A fresh object every
  // render is a changed dependency, which restarts the sampling loop that
  // depends on it - and that loop holds the previous frame. Rebuilt three
  // times a second, every frame is compared against nothing, no scene
  // change is ever seen, and the scanner fires once and then never again.
  return useMemo(() => ({ offer, rearm }), [offer, rearm]);
}
